/**
 * Basecamp Notes Modal Component
 * Displays generated Basecamp notes HTML with copy functionality
 */

import { useState, useEffect } from 'react';
import { Copy, Check, Loader2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { basecampApi } from '@/lib/basecamp-api';
import { toast } from 'sonner';

interface BasecampNotesModalProps {
  todoId?: string;
  html?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BasecampNotesModal({
  todoId,
  html: providedHtml,
  open,
  onOpenChange
}: BasecampNotesModalProps) {
  const [html, setHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      if (providedHtml) {
        // Use provided HTML directly
        setHtml(providedHtml);
        setIsLoading(false);
        setError(null);
      } else if (todoId) {
        // Load from API
        loadNotes();
      } else {
        setError('Either todoId or html must be provided');
      }
    } else {
      // Reset state when modal closes
      setHtml(null);
      setError(null);
      setCopied(false);
    }
  }, [open, todoId, providedHtml]);

  const loadNotes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await basecampApi.getBasecampNotes(todoId!);
      setHtml(data.html);
    } catch (err) {
      console.error('[BasecampNotesModal] Error loading notes:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate Basecamp notes');
      toast.error('Failed to generate notes', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!html) return;

    try {
      // Copy HTML content (Basecamp supports HTML)
      // Try modern clipboard API with HTML format
      if (navigator.clipboard && (navigator.clipboard as any).write) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([html.replace(/<[^>]*>/g, '')], { type: 'text/plain' })
        });
        await (navigator.clipboard as any).write([clipboardItem]);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        // Fallback: copy HTML as text (Basecamp will parse it)
        await navigator.clipboard.writeText(html);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = html;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopied(true);
      toast.success('Copied to clipboard', {
        description: 'Paste into Basecamp notes field'
      });
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[BasecampNotesModal] Error copying:', err);
      toast.error('Failed to copy', {
        description: 'Please select and copy manually'
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Basecamp Notes</DialogTitle>
          <DialogDescription>
            Copy the content below and paste it into Basecamp's notes field
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Generating notes...</span>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">Error</p>
                <p className="text-sm text-muted-foreground mt-1">{error}</p>
              </div>
            </div>
          )}

          {html && !isLoading && (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  Click the button below to copy all content
                </p>
                <Button
                  onClick={copyToClipboard}
                  variant="default"
                  size="sm"
                  className="gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy All
                    </>
                  )}
                </Button>
              </div>

              <div className="flex-1 overflow-auto border rounded-md bg-muted/30 p-6">
                <div
                  className="prose prose-sm max-w-none dark:prose-invert prose-p:text-foreground prose-h1:text-foreground prose-pre:text-xs prose-pre:font-mono prose-pre:text-foreground prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:p-3 prose-pre:rounded-md prose-pre:overflow-x-auto"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </div>

              <div className="text-xs text-muted-foreground border-t pt-3">
                <p className="font-medium mb-1">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Click "Copy All" to copy the content</li>
                  <li>Go to the Basecamp todo</li>
                  <li>Click "Edit" on the notes field</li>
                  <li>Paste the content (Cmd+V / Ctrl+V)</li>
                  <li>Save the changes</li>
                </ol>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

